// Add-ons: the six add-ons, one self-saving switch each, plus the
// attribution settings that only apply while White-label is in effect.
//
// Every switch PATCHes only its own key (PATCH /platform/leagues/:id/addons
// merges on the server), so two quick toggles never overwrite each other
// (#324). The switch flips at once and rolls back if the save fails.
// "Reset to package default" sends null for every key, which clears each
// override so the tenant follows its package again. Nothing here writes
// attribution except its own selects.

import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { ADDON_KEYS, ATTRIBUTION_KEYS, type AddonKey, type AttributionKey, type AttributionSetting, type TenantDetail } from '@/api/types'
import {
  AccountStatusPill,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EditableSelectField,
  KeyValueList,
  PlanPill,
  ToggleRow,
  useToast,
} from '@/components/ui'
import { ADDONS, ATTRIBUTION_DESCRIPTIONS, ATTRIBUTION_LABELS, addonsDifferingFrom, packageDefaultAddons, whiteLabelInEffect } from '@/lib/addons'
import { formatDate } from '@/lib/format'
import { useTenant } from '../tenantContext'

const BILLING_LABEL = { stripe: 'Stripe', manual: 'Manual (no Stripe)', none: 'None' } as const

export default function AddonsTab() {
  const { tenant, apply, reload } = useTenant()
  const toast = useToast()
  const [resetOpen, setResetOpen] = useState(false)
  // Saves in flight, and whether any overlapped. Each switch sends only its
  // own key and the server merges, but overlapping answers can arrive out of
  // order; once they all settle, one refetch shows the server's final map.
  const inFlight = useRef(0)
  const overlapped = useRef(false)

  const saveAddon = async (key: AddonKey, next: boolean) => {
    inFlight.current += 1
    if (inFlight.current > 1) overlapped.current = true
    try {
      apply(await api.tenants.updateAddons(tenant.id, { [key]: next }))
    } finally {
      inFlight.current -= 1
      if (inFlight.current === 0 && overlapped.current) {
        overlapped.current = false
        reload()
      }
    }
  }

  const defaults = packageDefaultAddons(tenant)
  const differing = addonsDifferingFrom(tenant.addons, defaults)
  const packageLabel = tenant.account.package_key
    ? tenant.account.package_key === 'standard'
      ? 'Standard'
      : tenant.account.package_name || tenant.account.package_key
    : null

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_1fr]">
      <Card>
        <CardHeader
          title="Paid add-ons"
          description="Each switch saves on its own. Everything else is included for every league."
          actions={
            // Always available: an add-on can be pinned to the value its package
            // gives anyway, and only a reset makes it follow the package again.
            <Button size="sm" onClick={() => setResetOpen(true)}>
              Reset to package default
            </Button>
          }
        />
        <div>
          {ADDON_KEYS.map((key) => (
            <ToggleRow
              key={key}
              label={ADDONS[key].label}
              description={ADDONS[key].description}
              checked={tenant.addons[key]}
              note={
                key === 'white_label' && tenant.custom_domain && tenant.addons.white_label ? (
                  <span className="text-xs text-muted">
                    The custom domain <span className="break-all font-mono">{tenant.custom_domain}</span> needs this.
                  </span>
                ) : defaults[key] !== tenant.addons[key] ? (
                  <span className="text-xs text-subtle">{defaults[key] ? 'Included in the package, turned off here' : 'Not in the package, turned on here'}</span>
                ) : undefined
              }
              onToggle={(next) => saveAddon(key, next)}
            />
          ))}
        </div>
      </Card>

      <div className="grid content-start gap-4">
        <Card>
          <CardHeader title="Plan" actions={<Link to="../plan" className="text-xs font-semibold text-accent hover:underline">Manage</Link>} />
          <KeyValueList
            items={[
              { label: 'Status', value: <AccountStatusPill account={tenant.account} /> },
              { label: 'Package', value: <PlanPill account={tenant.account} /> },
              { label: 'Billing', value: BILLING_LABEL[tenant.account.billing_source] },
              tenant.account.status === 'trialing'
                ? { label: 'Trial ends', value: formatDate(tenant.account.trial_ends_at) }
                : { label: 'Next invoice', value: formatDate(tenant.account.current_period_end) },
            ]}
          />
        </Card>
        <AttributionCard tenant={tenant} apply={apply} />
      </div>

      <ConfirmDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        tone="primary"
        title="Reset add-ons?"
        description={`Clears every add-on override; the tenant follows ${packageLabel ? `the ${packageLabel} package` : 'its package (none, so every add-on is off)'}, including future changes to it. ${
          differing.length ? 'These change now:' : 'Nothing changes right now.'
        }`}
        confirmLabel={differing.length ? `Change ${differing.length} add-on${differing.length === 1 ? '' : 's'}` : 'Clear overrides'}
        onConfirm={async () => {
          const patch = Object.fromEntries(ADDON_KEYS.map((k) => [k, null]))
          apply(await api.tenants.updateAddons(tenant.id, patch))
          toast.success('Add-ons now follow the package.')
        }}
      >
        {differing.length ? (
          <ul className="grid gap-1.5 text-[13px]" aria-label="Add-ons that change">
            {differing.map((k) => (
              <li key={k} className="flex items-center justify-between gap-3 rounded-md border border-line bg-raised/40 px-3 py-2">
                <span className="font-semibold">{ADDONS[k].label}</span>
                <span className="text-muted">
                  {tenant.addons[k] ? 'On' : 'Off'} → <span className="font-semibold text-ink">{defaults[k] ? 'On' : 'Off'}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </ConfirmDialog>
    </div>
  )
}

type OverrideChoice = 'follow' | 'show' | 'hide'
// A stored value equal to the launch default behaves exactly like no stored
// value (and every existing league has one stored: migration 047), so both
// read as "Default".
const toChoice = (s: AttributionSetting): OverrideChoice => (s.override === null || s.override === s.derived ? 'follow' : s.override ? 'show' : 'hide')
const fromChoice = (c: string): boolean | null => (c === 'show' ? true : c === 'hide' ? false : null)

function AttributionCard({ tenant, apply }: { tenant: TenantDetail; apply: (t: TenantDetail) => void }) {
  const inEffect = whiteLabelInEffect(tenant)
  return (
    <Card>
      <CardHeader
        title="Attribution"
        description="These settings apply only while White-label is on. Otherwise the footer credit shows and the share-card watermark and corner badge stay hidden."
      />
      <p className="mb-3 text-[13px]">
        {inEffect ? (
          <>
            <span className="font-semibold">White-label is on</span>, so these settings are in effect.
          </>
        ) : tenant.addons.white_label ? (
          <>
            <span className="font-semibold">White-label is off</span>: it's turned on, but the account isn't live, so the public site uses the defaults.
          </>
        ) : (
          <>
            <span className="font-semibold">White-label is off</span>, so the public site uses the defaults.
          </>
        )}
      </p>
      <div className="grid gap-4">
        {ATTRIBUTION_KEYS.map((k) => (
          <AttributionRow key={k} k={k} tenant={tenant} inEffect={inEffect} apply={apply} />
        ))}
      </div>
    </Card>
  )
}

function EffectiveBadge({ shown }: { shown: boolean }) {
  return shown ? <Badge tone="accent">Showing now</Badge> : <Badge tone="neutral">Hidden now</Badge>
}

function AttributionRow({ k, tenant, inEffect, apply }: { k: AttributionKey; tenant: TenantDetail; inEffect: boolean; apply: (t: TenantDetail) => void }) {
  const s = tenant.attribution[k]
  const choice = toChoice(s)
  return (
    <EditableSelectField
      label={
        <span className="inline-flex flex-wrap items-center gap-2">
          {ATTRIBUTION_LABELS[k]} <EffectiveBadge shown={s.effective} />
        </span>
      }
      hint={
        choice !== 'follow' && !inEffect ? (
          <>
            {ATTRIBUTION_DESCRIPTIONS[k]}.{' '}
            <span className="text-warn">Set to {choice === 'show' ? 'show' : 'hide'}, but that only applies while White-label is on.</span>
          </>
        ) : (
          ATTRIBUTION_DESCRIPTIONS[k]
        )
      }
      value={choice}
      // "Default" is the launch default; the other option is the only
      // choice that changes anything (and only with White-label).
      options={[
        { value: 'follow', label: `Default (${s.derived ? 'shown' : 'hidden'})` },
        s.derived ? { value: 'hide', label: 'Hide with White-label' } : { value: 'show', label: 'Show with White-label' },
      ]}
      onSave={async (next) => {
        apply(await api.tenants.updateAttribution(tenant.id, { [k]: fromChoice(next) }))
      }}
    />
  )
}
